import { JSX, MouseEvent, useState } from 'react';
import { Confirm, useNotify, useRecordContext } from 'react-admin';
import {
    Alert,
    Autocomplete,
    Box,
    Chip,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { Delete as DeleteIcon, WarningAmber as WarningIcon } from '@mui/icons-material';
import { changeSeat, SeatChangeReport, useAccountSeats } from './hooks/customer-account-seats';
import { CustomerAccountAllowanceBar } from './CustomerAccountAllowanceBar';
import { useCustomerAccountAllowances } from './CustomerAccountAllowanceProvider';
import {
    addSeatBlockedReason,
    addSeatGuidance,
    invitationExpiry,
    overSeatAllowance,
    personLabel,
    SeatChange,
    seatChangeConfirmation,
    SeatInvitation,
    SeatPerson,
    seatRoleLabel
} from './seat-panel-format';

type PendingChange = { change: SeatChange, person: SeatPerson };

/**
 * Seat management for a customer account.
 *
 * **What was here before**, and why this replaced it (2026-09-10, at the user's
 * instruction): one autocomplete over every customer in the database, and a grid cell
 * containing the word `right`. It showed no allowance, so an admin could not see whether
 * a seat was available; it did not show the account owner, who holds a seat and is not in
 * the list, so the count read as off by one; it showed no roles and no outstanding
 * invitations; and because it offered every customer there was, it would seat one
 * company's staff on another company's account.
 *
 * **Each change is made the moment it is confirmed** (HQAPI-92, 2026-09-14). This panel used
 * to edit the form's `seatHolders` field and wait for Save; it now calls HQ-API's grant and
 * revoke routes, and the account form no longer carries seats at all.
 *
 * **The subscription's limit comes first** (user, 2026-09-14). The allowance band shows it,
 * the picker closes with the reason when no seat is left, an account already over the limit
 * says so, and HQ-API refuses a grant on the same numbers. A seat can always be removed.
 */
export const CustomerAccountSeatsPanel: () => JSX.Element = (): JSX.Element => {
    const record: any = useRecordContext();
    const notify = useNotify();
    const { loadingSeats, seatsError, seats, reloadSeats } = useAccountSeats(record?.id);
    const { loadingAllowances, allowancesError, allowances, reloadAllowances } = useCustomerAccountAllowances();
    const [search, setSearch] = useState<string>('');
    const [pending, setPending] = useState<PendingChange|null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [changeError, setChangeError] = useState<string>('');

    const holders: SeatPerson[] = seats?.seatHolders ?? [];
    // The API leaves out anyone who already holds a seat or has an invitation outstanding.
    const available: SeatPerson[] = seats?.candidates ?? [];

    const blockedReason: string|undefined = addSeatBlockedReason({
        loading: loadingSeats,
        headroom: allowances?.seats?.headroom,
        candidates: available,
        verifiedDomains: seats?.verifiedDomains ?? [],
        search,
    });
    const overAllowance: string|undefined = overSeatAllowance(allowances?.seats);
    const confirmation = pending === null ? null : seatChangeConfirmation(pending.change, pending.person);

    function ask(change: SeatChange, person: SeatPerson): void {
        // Focus left on the control underneath is hidden from assistive technology while the
        // dialog is open, and Chrome logs "Blocked aria-hidden" for it (HQ-50).
        (document.activeElement as HTMLElement|null)?.blur();
        setChangeError('');
        setPending({ change, person });
    }

    async function applyPending(): Promise<void> {
        if (pending === null || record?.id === undefined) {
            return;
        }
        const { change, person } = pending;
        setPending(null);
        setSaving(true);
        const report: SeatChangeReport = await changeSeat(change, record.id, person.id);
        setSaving(false);

        if (report.status !== 'success') {
            setChangeError(report.message);
            return;
        }
        notify(report.message, { type: 'info' });
        // Both, so the band and the picker's headroom are current before the next change. The
        // search goes back to empty, or the list would reload filtered to whoever was just picked.
        setSearch('');
        await Promise.all([reloadSeats(''), reloadAllowances()]);
    }

    if (seatsError !== '') {
        return (
            <Alert severity="warning" sx={ { width: '100%' } }>
                { seatsError }
            </Alert>
        );
    }

    return (
        <Box sx={ { width: '100%' } }>
            <CustomerAccountAllowanceBar allowances={ allowances }
                                         loading={ loadingAllowances }
                                         error={ allowancesError }
                                         pools={ ['seats'] } />

            { (overAllowance !== undefined) &&
                <Alert severity="error" sx={ { width: '100%', marginBottom: '14px' } }>
                    { overAllowance }
                </Alert>
            }

            { (changeError !== '') &&
                <Alert severity="error" sx={ { width: '100%', marginBottom: '14px' } } onClose={ (): void => setChangeError('') }>
                    { changeError }
                </Alert>
            }

            { (loadingSeats || saving) && <LinearProgress sx={ { margin: '10px 0', width: '100%' } } /> }

            <SectionHeading text="Who holds a seat" />
            <Table size="small" sx={ { marginBottom: '18px' } }>
                <TableHead>
                    <TableRow>
                        <TableCell>{ 'Name' }</TableCell>
                        <TableCell>{ 'Email' }</TableCell>
                        <TableCell>{ 'Role' }</TableCell>
                        <TableCell align="right">{ '' }</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { (seats?.owner !== null && seats?.owner !== undefined) &&
                        <TableRow>
                            <TableCell>{ personLabel(seats.owner) }</TableCell>
                            <TableCell>{ seats.owner.email }</TableCell>
                            <TableCell>{ seats.owner.roleLabel ?? seatRoleLabel(seats.owner.role) }</TableCell>
                            <TableCell align="right">
                                { /* The owner's seat is counted but cannot be given up: it goes
                                     with the account, and leaving means deleting or transferring
                                     it. Saying so here is what makes the count add up on screen. */ }
                                <Tooltip title="The account owner holds a seat that cannot be removed. Transfer or close the account instead.">
                                    <Chip size="small" label="Owner, holds a seat" />
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    }
                    { holders.map((person: SeatPerson): JSX.Element => (
                        <TableRow key={ `seat_${ person.id }` }>
                            <TableCell>{ personLabel(person) }</TableCell>
                            <TableCell>
                                { person.email ?? '' }
                                { (person.onVerifiedDomain === false) &&
                                    <Tooltip title="This address is not on any of the account's verified domains. Allowed, and worth knowing: seating one customer's staff on another's account gives them that account's data.">
                                        <WarningIcon sx={ { fontSize: '16px', marginLeft: '6px', color: 'var(--wf-live-line)', verticalAlign: 'text-bottom' } } />
                                    </Tooltip>
                                }
                            </TableCell>
                            <TableCell>{ person.roleLabel ?? seatRoleLabel(person.role) }</TableCell>
                            <TableCell align="right">
                                <Tooltip title="Remove this seat holder">
                                    <span>
                                        <IconButton size="small"
                                                    disabled={ saving }
                                                    aria-label={ `Remove ${ personLabel(person) }'s seat` }
                                                    onClick={ (event: MouseEvent<HTMLButtonElement>): void => {
                                                        event.currentTarget.blur();
                                                        ask('revoke', person);
                                                    } }>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    )) }
                    { (holders.length === 0 && !loadingSeats) &&
                        <TableRow>
                            <TableCell colSpan={ 4 } sx={ { color: 'text.secondary' } }>
                                { 'Nobody but the account owner holds a seat on this account.' }
                            </TableCell>
                        </TableRow>
                    }
                </TableBody>
            </Table>

            <SectionHeading text="Give somebody a seat" />
            <Box sx={ { maxWidth: '520px', marginBottom: '6px' } }>
                <Autocomplete
                    options={ available }
                    disabled={ allowances?.seats?.headroom === false || saving }
                    value={ null }
                    blurOnSelect
                    // The API caps the list and the search narrows it, so the filtering
                    // happens there rather than over an already-complete list here.
                    filterOptions={ (options: SeatPerson[]): SeatPerson[] => options }
                    inputValue={ search }
                    onInputChange={ (_event: unknown, value: string, reason: string): void => {
                        // Only what the admin typed, or clearing it, is a search. Picking an option
                        // fires this with the option's label ('selectOption'), which used to become
                        // the search and empty the list: "No customer matches that search."
                        if (reason !== 'input' && reason !== 'clear') {
                            return;
                        }
                        setSearch(value);
                        reloadSeats(value);
                    } }
                    noOptionsText={ blockedReason ?? 'No customer matches that search.' }
                    getOptionLabel={ (person: SeatPerson): string => `${ personLabel(person) } (${ person.email ?? '' })` }
                    isOptionEqualToValue={ (a: SeatPerson, b: SeatPerson): boolean => a.id === b.id }
                    onChange={ (_event: unknown, person: SeatPerson|null): void => {
                        if (person !== null) {
                            ask('grant', person);
                        }
                    } }
                    renderOption={ (props: any, person: SeatPerson): JSX.Element => (
                        <li { ...props } key={ `candidate_${ person.id }` }>
                            <span>{ `${ personLabel(person) } (${ person.email ?? '' })` }</span>
                            { (person.onVerifiedDomain === false) &&
                                <Tooltip title="Not on one of this account's verified domains">
                                    <WarningIcon sx={ { fontSize: '16px', marginLeft: '8px', color: 'var(--wf-live-line)' } } />
                                </Tooltip>
                            }
                        </li>
                    ) }
                    renderInput={ (params: any): JSX.Element => (
                        <TextField { ...params } label="Add a seat holder" size="small" />
                    ) }
                />
            </Box>
            <Typography sx={ { fontSize: '13px', color: 'text.secondary', marginBottom: '18px' } }>
                { blockedReason ?? addSeatGuidance(seats?.verifiedDomains ?? []) }
            </Typography>

            <SectionHeading text="Invitations not yet accepted" />
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{ 'Name' }</TableCell>
                        <TableCell>{ 'Email' }</TableCell>
                        <TableCell>{ 'Role' }</TableCell>
                        <TableCell>{ 'Expiry' }</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { (seats?.invitations ?? []).map((invitation: SeatInvitation, index: number): JSX.Element => {
                        const expiry: { text: string, expired: boolean } = invitationExpiry(invitation.expires);

                        return (
                            <TableRow key={ `invitation_${ invitation.id ?? index }` }>
                                <TableCell>{ invitation.name ?? '' }</TableCell>
                                <TableCell>{ invitation.email ?? '' }</TableCell>
                                <TableCell>{ seatRoleLabel(invitation.role) }</TableCell>
                                <TableCell sx={ { color: expiry.expired ? 'var(--wf-bad)' : 'inherit' } }>
                                    { expiry.text }
                                </TableCell>
                            </TableRow>
                        );
                    }) }
                    { ((seats?.invitations ?? []).length === 0 && !loadingSeats) &&
                        <TableRow>
                            <TableCell colSpan={ 4 } sx={ { color: 'text.secondary' } }>
                                { 'No invitations are outstanding.' }
                            </TableCell>
                        </TableRow>
                    }
                </TableBody>
            </Table>
            { ((seats?.invitations ?? []).length > 0) &&
                <Typography sx={ { fontSize: '13px', color: 'text.secondary', marginTop: '6px' } }>
                    { /* HQAPI-88: an offered seat is reserved. This said the opposite until 2026-09-14. */ }
                    { 'An invitation that has not expired holds one of the seats counted above until it is accepted. '
                        + 'They are sent from the customer portal and are cancelled there.' }
                </Typography>
            }

            <Confirm
                isOpen={ confirmation !== null }
                title={ confirmation?.title ?? '' }
                content={ confirmation?.content ?? '' }
                confirm={ confirmation?.confirm }
                onConfirm={ (): void => { void applyPending(); } }
                onClose={ (): void => setPending(null) }
            />
        </Box>
    );
}

const SectionHeading: (props: { text: string }) => JSX.Element = (props: { text: string }): JSX.Element => (
    <Typography sx={ {
        fontSize: 'var(--wf-eyebrow)',
        letterSpacing: 'var(--wf-track)',
        textTransform: 'uppercase',
        color: 'text.secondary',
        fontWeight: 700,
        marginBottom: '6px',
    } }>
        { props.text }
    </Typography>
);
